import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { TagInput } from '@containers/form/TagInput';
import { UNIVERSE_KINDS } from '@services/scoreBackfills';
import { formatIndex } from './format';
import { SelectableCard } from './SelectableCard';

const SelectWrapper = styled.div`
  width: 100%;

  select {
    width: 100%;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    border-radius: 4px;
    padding: 0 8px;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
    color: ${({ theme }) => theme.themeColors.text};
  }
`;

const KindGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PasteArea = styled.textarea`
  width: 100%;
  min-height: 64px;
  resize: vertical;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-family: inherit;
  font-size: 14px;
`;

const CounterText = styled(Body5)`
  color: ${({ theme, $atCap }) => ($atCap ? theme.themeColors.error : 'inherit')};
`;

const KIND_GLOSS = {
  [UNIVERSE_KINDS.WHOLE_CORPUS]: 'Every company we cover.',
  [UNIVERSE_KINDS.INDEX]: 'Companies in a named index.',
  [UNIVERSE_KINDS.TICKERS]: 'A specific list of symbols you provide.',
};

export const UniversePicker = ({
  kind,
  onKindChange,
  indexValue,
  onIndexChange,
  tickerTags,
  onAddTicker,
  onAddTickers,
  onRemoveTicker,
  universeOptions,
}) => {
  const indices = universeOptions?.indices || [];
  const wholeCorpusAllowed = universeOptions?.whole_corpus_allowed !== false;
  const maxTickers = universeOptions?.max_tickers || 1000;

  const [pasteValue, setPasteValue] = React.useState('');
  const [pasteFeedback, setPasteFeedback] = React.useState('');

  const atCap = tickerTags.length >= maxTickers;

  const handleBulkAdd = () => {
    const symbols = pasteValue
      .split(/[\s,;\n]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    if (symbols.length === 0) {
      setPasteValue('');
      setPasteFeedback('');
      return;
    }
    const { added = 0, duplicates = 0, overCap = 0 } = onAddTickers(symbols) || {};
    setPasteValue('');

    const parts = [];
    if (added) parts.push(`Added ${added}.`);
    if (duplicates) parts.push(`${duplicates} duplicates skipped.`);
    if (overCap) parts.push(`${overCap} over the ${maxTickers}-ticker cap ignored.`);
    setPasteFeedback(parts.join(' '));
  };

  const renderKindCard = (cardKind, label) => (
    <SelectableCard selected={kind === cardKind}>
      <input
        type="radio"
        name="universeKind"
        checked={kind === cardKind}
        onChange={() => onKindChange(cardKind)}
      />
      <CardBody>
        <Body5>{label}</Body5>
        <Body7 isLowContrast>{KIND_GLOSS[cardKind]}</Body7>
      </CardBody>
    </SelectableCard>
  );

  return (
    <div>
      <KindGroup role="radiogroup" aria-label="Universe">
        {wholeCorpusAllowed && renderKindCard(UNIVERSE_KINDS.WHOLE_CORPUS, 'Whole corpus')}
        {renderKindCard(UNIVERSE_KINDS.INDEX, 'Index')}
        {renderKindCard(UNIVERSE_KINDS.TICKERS, 'Tickers')}
      </KindGroup>

      {kind === UNIVERSE_KINDS.INDEX && (
        <Container top={12}>
          <SelectWrapper>
            <select
              id="universe-index-select"
              aria-label="Index"
              value={indexValue || ''}
              onChange={(e) => onIndexChange(e.target.value)}
            >
              <option value={''}>Select an index…</option>
              {indices.map((ix) => (
                <option key={ix} value={ix}>
                  {formatIndex(ix)}
                </option>
              ))}
            </select>
          </SelectWrapper>
        </Container>
      )}

      {kind === UNIVERSE_KINDS.TICKERS && (
        <Container top={12}>
          <TagInput
            label={'Tickers'}
            tags={tickerTags}
            onAddTag={(t, n, v) => {
              if (!v) return;
              onAddTicker(v);
            }}
            onRemoveTag={onRemoveTicker}
          />
          <Container top={8}>
            <PasteArea
              aria-label="Paste tickers"
              value={pasteValue}
              onChange={(e) => setPasteValue(e.target.value)}
            />
            <Container top={8}>
              <SkeletonButton onClick={handleBulkAdd}>Add tickers</SkeletonButton>
            </Container>
            {pasteFeedback && (
              <Container top={4}>
                <Body7 isLowContrast>{pasteFeedback}</Body7>
              </Container>
            )}
          </Container>
          <Container top={4}>
            <CounterText isLowContrast={!atCap} $atCap={atCap}>
              {tickerTags.length} / {maxTickers} tickers
              {atCap && ' — maximum reached'}
            </CounterText>
          </Container>
        </Container>
      )}
    </div>
  );
};
